import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Plus, Edit, Save, X, Trash2 } from 'lucide-react';
import { formatCurrency } from '../utils/chartUtils';
import { forecastRevenueAPI, clientsAPI, resourcesAPI, resourceTypesAPI } from '../services/api';
import toast from 'react-hot-toast';
import errorHandler from '../utils/errorHandler';

const DEFAULT_UTILIZATION = 0.9; // Forecast revenue default 90% (editable)

const generateLast12Months = () => {
  const months = [];
  const currentDate = new Date();
  for (let i = 0; i < 12; i++) {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    months.push({ value: `${year}-${month}`, label: `${date.toLocaleString('default', { month: 'long' })} ${year}` });
  }
  return months;
};

const getLastMonth = () => {
  const d = new Date();
  const last = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, '0')}`;
};

const ForecastRevenue = () => {
  const [filteredData, setFilteredData] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);
  const [newEntry, setNewEntry] = useState({
    client_id: '',
    resource_id: '',
    resource_type_id: '',
    month_year: getLastMonth(),
    planned_days: 0,
    utilization_rate: DEFAULT_UTILIZATION,
    day_rate: 0,
    forecast_revenue: 0,
    frequency: 'monthly'
  });
  const [clients, setClients] = useState([]);
  const [resources, setResources] = useState([]);
  const [resourceTypes, setResourceTypes] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);
  // Initialise month filter directly — avoids race condition between init useEffect and loadData useEffect
  const [filters, setFilters] = useState({ month: getLastMonth(), client: '', resourceType: '', resource: '' });
  const [availableMonths] = useState(generateLast12Months());

  useEffect(() => {
    loadFormData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setNewEntry(prev => {
      const rate = prev.utilization_rate ?? DEFAULT_UTILIZATION;
      const rev = (prev.planned_days || 0) * rate * (prev.day_rate || 0);
      return { ...prev, forecast_revenue: prev.frequency === 'weekly' ? Math.round(rev * 4) : Math.round(rev) };
    });
  }, [newEntry.planned_days, newEntry.day_rate, newEntry.utilization_rate, newEntry.frequency]);

  useEffect(() => {
    if (editingEntry) {
      setEditingEntry(prev => {
        const rate = prev.utilization_rate ?? DEFAULT_UTILIZATION;
        const rev = (prev.planned_days || 0) * rate * (prev.day_rate || 0);
        return { ...prev, forecast_revenue: prev.frequency === 'weekly' ? Math.round(rev * 4) : Math.round(rev) };
      });
    }
  }, [editingEntry?.planned_days, editingEntry?.day_rate, editingEntry?.utilization_rate, editingEntry?.frequency]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadData();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.month) params.month = filters.month;
      if (filters.client) params.clientId = filters.client;
      if (filters.resourceType) params.resourceTypeId = filters.resourceType;
      if (filters.resource) params.resourceId = filters.resource;
      const res = await forecastRevenueAPI.getAll(params);
      setFilteredData(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load forecast revenue data');
    } finally {
      setLoading(false);
    }
  };

  const loadFormData = async () => {
    try {
      const [c, r, rt] = await Promise.all([clientsAPI.getAll(), resourcesAPI.getAll(), resourceTypesAPI.getAll()]);
      setClients(Array.isArray(c.data) ? c.data : []);
      setResources(Array.isArray(r.data) ? r.data : []);
      setResourceTypes(Array.isArray(rt.data) ? rt.data : []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load form data');
    }
  };

  const getDayRate = (clientId, resourceId, resourceTypeId) => {
    if (!clientId) return 0;
    const match = filteredData.find(
      x => String(x.client_id) === String(clientId) &&
        (resourceId ? String(x.resource_id) === String(resourceId) : (x.resource_id == null || x.resource_id === '')) &&
        (resourceTypeId ? String(x.resource_type_id) === String(resourceTypeId) : (x.resource_type_id == null || x.resource_type_id === ''))
    );
    return (match && (match.day_rate != null)) ? parseFloat(match.day_rate) : 0;
  };

  const handleClientChange = (clientId) => {
    setNewEntry(prev => ({ ...prev, client_id: clientId, day_rate: getDayRate(clientId, prev.resource_id, prev.resource_type_id) }));
  };
  const handleResourceChange = (resourceId) => {
    const res = resources.find(r => r.id === parseInt(resourceId));
    setNewEntry(prev => ({
      ...prev,
      resource_id: resourceId || '',
      resource_type_id: res?.resource_type_id ?? prev.resource_type_id,
      day_rate: getDayRate(prev.client_id, resourceId, res?.resource_type_id ?? prev.resource_type_id)
    }));
  };
  const handleResourceTypeChange = (resourceTypeId) => {
    setNewEntry(prev => ({ ...prev, resource_type_id: resourceTypeId || '', day_rate: getDayRate(prev.client_id, prev.resource_id, resourceTypeId) }));
  };

  const handleAdd = async () => {
    if (!newEntry.client_id) {
      toast.error('Please select a client');
      return;
    }
    if (!newEntry.resource_id && !newEntry.resource_type_id) {
      toast.error('Please select either a specific resource or a resource type');
      return;
    }
    const clientId = parseInt(newEntry.client_id, 10);
    if (Number.isNaN(clientId)) {
      toast.error('Please select a valid client');
      return;
    }
    try {
      await forecastRevenueAPI.create({
        clientId,
        resourceId: newEntry.resource_id ? parseInt(newEntry.resource_id, 10) : null,
        resourceTypeId: newEntry.resource_type_id ? parseInt(newEntry.resource_type_id, 10) : null,
        monthYear: newEntry.month_year,
        plannedDays: parseFloat(newEntry.planned_days),
        utilizationRate: parseFloat(newEntry.utilization_rate) ?? DEFAULT_UTILIZATION,
        dayRate: Math.round(parseFloat(newEntry.day_rate)),
        forecastRevenue: Math.round(parseFloat(newEntry.forecast_revenue)),
        frequency: newEntry.frequency,
        notes: newEntry.notes || ''
      });
      setNewEntry({
        client_id: '', resource_id: '', resource_type_id: '', month_year: getLastMonth(),
        planned_days: 0, utilization_rate: DEFAULT_UTILIZATION, day_rate: 0, forecast_revenue: 0, frequency: 'monthly'
      });
      setShowAddForm(false);
      toast.success('Forecast revenue added successfully');
      loadData();
    } catch (err) {
      toast.error('Failed to add: ' + errorHandler.getErrorMessage(err));
    }
  };

  const handleEdit = (item) => setEditingEntry({ ...item, utilization_rate: item.utilization_rate ?? DEFAULT_UTILIZATION, originalData: { ...item } });
  const handleCancelEdit = () => setEditingEntry(null);

  const handleSave = async (entry) => {
    try {
      await forecastRevenueAPI.update(entry.id, {
        plannedDays: parseFloat(entry.planned_days),
        utilizationRate: parseFloat(entry.utilization_rate) ?? DEFAULT_UTILIZATION,
        dayRate: Math.round(parseFloat(entry.day_rate)),
        forecastRevenue: Math.round(parseFloat(entry.forecast_revenue)),
        frequency: entry.frequency,
        notes: entry.notes || ''
      });
      setEditingEntry(null);
      toast.success('Forecast revenue updated successfully');
      loadData();
    } catch (err) {
      toast.error('Failed to update: ' + errorHandler.getErrorMessage(err));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this forecast revenue entry? This cannot be undone.')) return;
    try {
      await forecastRevenueAPI.delete(id);
      toast.success('Forecast revenue entry deleted');
      loadData();
    } catch (err) {
      toast.error('Failed to delete: ' + errorHandler.getErrorMessage(err));
    }
  };

  const getResourceTypeName = (id) => (resourceTypes.find(rt => rt.id === id) || {}).name || '-';

  if (loading && filteredData.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Forecast Revenue</h1>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4 mr-2 inline" />
          Add Forecast Revenue
        </button>
      </div>

      {showAddForm && (
        <Card>
          <CardHeader><CardTitle>Add Forecast Revenue</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                <select value={newEntry.client_id} onChange={(e) => handleClientChange(e.target.value)} className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500">
                  <option value="">Select Client</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Resource (optional)</label>
                <select value={newEntry.resource_id} onChange={(e) => handleResourceChange(e.target.value)} className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500">
                  <option value="">Select Resource</option>
                  {resources.map(r => <option key={r.id} value={r.id}>{r.name} ({getResourceTypeName(r.resource_type_id)})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Resource Type</label>
                <select value={newEntry.resource_type_id} onChange={(e) => handleResourceTypeChange(e.target.value)} className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500">
                  <option value="">Select Resource Type</option>
                  {resourceTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Month-Year</label>
                <select value={newEntry.month_year} onChange={(e) => setNewEntry({ ...newEntry, month_year: e.target.value })} className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500">
                  {availableMonths.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Planned Days</label>
                <input type="number" step="0.01" min="0" value={newEntry.planned_days} onChange={(e) => setNewEntry({ ...newEntry, planned_days: parseFloat(e.target.value) || 0 })} className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Utilization Rate (editable)</label>
                <input type="number" step="0.01" min="0" max="1" value={newEntry.utilization_rate} onChange={(e) => setNewEntry({ ...newEntry, utilization_rate: parseFloat(e.target.value) ?? DEFAULT_UTILIZATION })} className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500" title="e.g. 0.9 = 90%. Default 90%." />
                <p className="text-xs text-gray-500 mt-1">Default 90%. Editable.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Day Rate</label>
                <input type="number" step="0.01" min="0" value={newEntry.day_rate} onChange={(e) => setNewEntry({ ...newEntry, day_rate: parseFloat(e.target.value) || 0 })} className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
                <select value={newEntry.frequency} onChange={(e) => setNewEntry({ ...newEntry, frequency: e.target.value })} className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500">
                  <option value="monthly">Monthly</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Forecast Revenue (from utilisation)</label>
                <input type="number" value={newEntry.forecast_revenue} readOnly className="w-full p-2 border border-gray-300 rounded bg-gray-50" />
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <button onClick={() => setShowAddForm(false)} className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
              <button onClick={handleAdd} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Add</button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle>Filters</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
              <select value={filters.month} onChange={(e) => setFilters({ ...filters, month: e.target.value })} className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500">
                <option value="">All Months</option>
                {availableMonths.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
              <select value={filters.client} onChange={(e) => setFilters({ ...filters, client: e.target.value })} className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500">
                <option value="">All Clients</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Resource Type</label>
              <select value={filters.resourceType} onChange={(e) => setFilters({ ...filters, resourceType: e.target.value })} className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500">
                <option value="">All</option>
                {resourceTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Resource</label>
              <select value={filters.resource} onChange={(e) => setFilters({ ...filters, resource: e.target.value })} className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500">
                <option value="">All</option>
                {resources.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Forecast Revenue Details</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Client</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Month</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Resource</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Resource Type</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Frequency</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Planned Days</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Utilization</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Day Rate</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Forecast Revenue</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((item) => (
                  <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">{item.client_name || '-'}</td>
                    <td className="py-3 px-4">{item.month_year}</td>
                    <td className="py-3 px-4">{item.person_name || '-'}</td>
                    <td className="py-3 px-4">{item.resource_type_name || '-'}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.frequency === 'weekly' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
                        {item.frequency === 'weekly' ? 'Weekly' : 'Monthly'}
                      </span>
                    </td>
                    <td className="py-3 px-4">{parseFloat(item.planned_days).toFixed(2)}</td>
                    <td className="py-3 px-4">{item.utilization_rate != null ? `${(parseFloat(item.utilization_rate) * 100).toFixed(0)}%` : `${(DEFAULT_UTILIZATION * 100).toFixed(0)}%`}</td>
                    <td className="py-3 px-4">{formatCurrency(item.day_rate)}</td>
                    <td className="py-3 px-4">{formatCurrency(item.forecast_revenue)}</td>
                    <td className="py-3 px-4">
                      <button onClick={() => handleEdit(item)} className="text-blue-600 hover:text-blue-900 mr-2" title="Edit"><Edit className="h-4 w-4 inline" /></button>
                      <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-900" title="Delete"><Trash2 className="h-4 w-4 inline" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {editingEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Edit Forecast Revenue</h3>
              <button onClick={handleCancelEdit} className="text-gray-500 hover:text-gray-700"><X className="h-6 w-6" /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Planned Days</label>
                <input type="number" step="0.01" min="0" value={editingEntry.planned_days} onChange={(e) => setEditingEntry({ ...editingEntry, planned_days: parseFloat(e.target.value) || 0 })} className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Utilization Rate (editable)</label>
                <input type="number" step="0.01" min="0" max="1" value={editingEntry.utilization_rate ?? DEFAULT_UTILIZATION} onChange={(e) => setEditingEntry({ ...editingEntry, utilization_rate: parseFloat(e.target.value) ?? DEFAULT_UTILIZATION })} className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Day Rate</label>
                <input type="number" step="0.01" min="0" value={editingEntry.day_rate} onChange={(e) => setEditingEntry({ ...editingEntry, day_rate: parseFloat(e.target.value) || 0 })} className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
                <select value={editingEntry.frequency} onChange={(e) => setEditingEntry({ ...editingEntry, frequency: e.target.value })} className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500">
                  <option value="monthly">Monthly</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Forecast Revenue</label>
                <input type="number" value={editingEntry.forecast_revenue} readOnly className="w-full p-2 border border-gray-300 rounded bg-gray-50" />
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <button onClick={handleCancelEdit} className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
              <button onClick={() => handleSave(editingEntry)} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"><Save className="h-4 w-4 mr-2 inline" /> Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ForecastRevenue;
